<script setup lang="ts">
import UserCustomFieldAddEditDialog from '@/components/dialogs/UserCustomFieldAddEditDialog.vue'
import { Modules } from '@/types/generated'
import UserCustomFieldData = Modules.UserCustomField.UserCustomFieldData
import UserCustomFieldTypeEnum = Modules.UserCustomField.Enums.UserCustomFieldTypeEnum

definePage({
  meta: {
    action: 'read',
    subject: 'user-custom-fields',
  },
})

const { t } = useI18n()

const headers = [
  { title: t('user-custom-fields-page.title'), key: 'title' },
  { title: t('user-custom-fields-page.type'), key: 'type' },
  { title: t('user-custom-fields-page.options_count'), key: 'options', sortable: false },
  { title: t('user-custom-fields-page.actions'), key: 'actions', sortable: false },
]

const isLoading = ref(false)
const isAddDialogOpen = ref(false)
const isDeleteConfirmDialogVisible = ref(false)
const currentItem = ref<UserCustomFieldData | null>(null)
const itemForDelete = ref<UserCustomFieldData | null>(null)

// The index endpoint returns the whole collection (capped at 20 fields server-side),
// so the table paginates on the client and needs no cookie-backed options.
const { data: itemsData, execute: fetchItems } = await useApi<UserCustomFieldData[]>('/user-custom-fields/')

const items = computed<UserCustomFieldData[]>(() => itemsData.value ?? [])

const typeLabel = (type: UserCustomFieldTypeEnum): string => t(`user-custom-fields-page.type_${type}`)

const optionsCount = (field: UserCustomFieldData): string =>
  (field.type === UserCustomFieldTypeEnum.Select ? String(field.options.length) : '—')

const updateList = async () => {
  isLoading.value = true
  await fetchItems()
  isLoading.value = false
}

const showAddDialog = () => {
  currentItem.value = null
  isAddDialogOpen.value = true
}

const showEditDialog = (item: UserCustomFieldData) => {
  currentItem.value = item
  isAddDialogOpen.value = true
}

const deleteConfirmation = (item: UserCustomFieldData) => {
  itemForDelete.value = item
  isDeleteConfirmDialogVisible.value = true
}

const deleteItem = async () => {
  if (!itemForDelete.value?.id)
    return

  await $api(`/user-custom-fields/${itemForDelete.value.id}`, { method: 'DELETE' })
  itemForDelete.value = null
  await updateList()
}
</script>

<template>
  <div>
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <VSpacer />
        <div class="d-flex align-center flex-wrap gap-4">
          <VBtn
            prepend-icon="tabler-plus"
            @click="showAddDialog"
          >
            {{ t('user-custom-fields-page.add') }}
          </VBtn>
        </div>
      </VCardText>
      <div class="category-table">
        <VDataTable
          :headers="headers"
          :items="items"
          :loading="isLoading"
          :loading-text="t('loading')"
          :items-per-page="25"
          class="text-no-wrap"
        >
          <template #item.type="{ item }">
            {{ typeLabel(item.type) }}
          </template>

          <template #item.options="{ item }">
            {{ optionsCount(item) }}
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex align-items-end justify-end">
              <IconBtn>
                <VIcon
                  icon="tabler-edit"
                  :title="t('user-custom-fields-page.edit')"
                  @click="showEditDialog(item)"
                />
              </IconBtn>
              <IconBtn>
                <VIcon
                  icon="tabler-trash"
                  :title="t('user-custom-fields-page.delete')"
                  @click="deleteConfirmation(item)"
                />
              </IconBtn>
            </div>
          </template>

          <template #no-data>
            <div class="text-center text-disabled py-6">
              {{ t('user-custom-fields-page.empty') }}
            </div>
          </template>
        </VDataTable>
      </div>
    </VCard>

    <UserCustomFieldAddEditDialog
      v-model:isDialogVisible="isAddDialogOpen"
      :field="currentItem"
      @submit="updateList"
    />

    <ConfirmDialog
      v-model:isDialogVisible="isDeleteConfirmDialogVisible"
      :confirmation-question="t('user-custom-fields-page.delete_confirm')"
      cancel-msg=""
      :cancel-title="t('confirmation.cancel')"
      :confirm-msg="t('confirmation.deleted')"
      :confirm-title="t('confirmation.success')"
      @confirm="deleteItem"
    />
  </div>
</template>
